import React from 'react';
import { observable, decorate, action, computed } from 'mobx';
import { debounce } from 'lodash'

// core
import { STORAGE_KEY, API_METHOD } from 'config';
import { Polling } from '_core/hooks/useRequest';
import { getValue, setValue } from '_core/utils/storage';
import { RootStoreContext } from '_core/storeMobx/RootStore';


export const useCartContext = () => React.useContext(RootStoreContext)?.cartStore;

export class CartStore {
  
  goods = [];
  isFetched = false;
  lastModified = 0;
  modifiable = 0;
  count = 0;
  total = 0;
  currency = null;
  replaceCurrency = undefined;
  price = 0.00;
  discount = 0.00;
  points = 0.0;
  object = null;
  message = null;
  isNext = false;
  isVisible = false;
  
  modalRef = null;
  modalBtnRef = null;
  
  
  get isEmpty() {
    return !this.length;
  }
  
  get length() {
    return this.goods.length;
  }
  
  get agreement_article_list() {
    return [...new Set(this.goods.flatMap((item) => item.agreement_article_list?.length && [...item.agreement_article_list] || null).filter((item) => !!item))];
  }
  
  get is_delivery_required() {
    return this.goods.some((item) => item.is_delivery_required);
  }
  
  get amount() {
    return this.price - this.discount;
  }
  
  
  constructor() {
    this.load();
  }
  
  setFetched(value) {
    this.isFetched = value;
  }
  
  setVisible(value) {
    this.isVisible = value;
  }
  
  setReplaceCurrency(value) {
    this.replaceCurrency = value || undefined;
  }
  
  find(id) {
    return this.goods
      .find((item) => item.id == id);
  }
  
  getCount(goods) {
    return this.find(goods.goods?.id || goods.id)?.count || 0;
  }
  
  clear() {
    const clearTask = () => {
      if (!this.isFetched)
        setTimeout(clearTask, 1000);
      
      this.clearSubmit();
    };
    
    setTimeout(clearTask, 1);
  }
  
  clearSubmit() {
    this.goods.length = 0;
    
    this.lastModified = Date.now();
    
    this.submit();
  }
  
  clearMessage() {
    this.message = null;
  }
  
  add(goods, count = 1) {
    count = Number(count);
    
    if (!count || count < 0)
      return;
    
    let item = this.find(goods.goods?.id || goods.id);
    
    if (!item) {
      this.goods.push(this._buildGoodsItem(goods));
      
      item = this.goods[this.goods.length - 1];
    }
    
    let group = item.group.find((gr) => gr.is_modifiable);
    
    if (!group) {
      item.group.unshift({ 
          count: 0, 
          amount: goods.amount.value, 
          points: goods.points, 
          is_modifiable: true 
        });
      
      group = item.group[0];
    }
    
    item.count += count;
    item.total += count;
    group.count += count;
    
    this.lastModified = Date.now();
    
    this._calcValues();
    
    this.submit();
  }
  
  set(goods, count) {
    count = Number(count) || 0;
    
    let item = this.find(goods.goods?.id || goods.id);
    
    if (count > 0) {
      if (!item || count > item.count)
        return this.add(goods, count - item.count);
      
      let rest = item.count - count;
      
      item.count -= rest;
      item.total -= rest;
      
      item.group = item.group.filter((gr) => {
        let sub = gr.is_modifiable && Math.min(gr.count, rest) || 0;
        
        gr.count -= sub;
        rest -= sub;
        
        return gr.count > 0;
      });
    } else {
      this.goods.remove(item);
    }
    
    this.lastModified = Date.now();
    
    this._calcValues();
    
    this.submit();
  }
  
  load() {
    let goods_list = getValue(STORAGE_KEY.CART) || [];
    
    if (Array.isArray(goods_list)) {
      goods_list = goods_list.map((item) => {
        const result = this._buildGoodsItem(item);
        
        result.count = Number(item.count) || 0;
        result.total = Number(item.total) || 0;
        
        if (Array.isArray(item.group))
          result.group = item.group;
        
        return result;
      });
    } else {
      goods_list = [];
    }
    
    this.goods.length = 0;
    this.goods.push(...goods_list);
    
    this.lastModified = Date.now();
    
    this._calcValues();
  }
  
  store() {
    const goods = this.goods;
    
    if (goods.length) {
      setValue(STORAGE_KEY.CART, goods);
    } else {
      setValue(STORAGE_KEY.CART)
    }
  }
  
  fetch() {
    this.setFetched(false);
    
    this.load();
    
    Polling.queue(API_METHOD.CART, { currency: this.replaceCurrency })
      .then((data) => this._syncData(data));
    
    Polling.queue(API_METHOD.OBJECT_BY_CITY, {})
      .then((data) => this._setObject(data?.object));
  }
  
  buildApiList() {
    return this.goods
      .filter((item) => item.count > 0)
      .map(item => ({ goods_id: item.id, count: item.count }));
  }
  
  submit = debounce(() => {
    this.store();
    
    let lastModified = this.lastModified;
    
    Polling.queue(API_METHOD.CART_UPDATE, { currency: this.replaceCurrency, cart: this.buildApiList() })
      .then((data) => lastModified === this.lastModified && this._syncData(data));
  }, 200);
  
  setModalRef = (value) => this.modalRef = value;
  
  setModalBtnRef = (value) => this.modalBtnRef = value;
  
  isModal = (el) => this.modalRef && this.modalRef.contains(el) || this.modalBtnRef && this.modalBtnRef.contains(el);
  
  
  _calcValues() {
    this.modifiable = 0;
    this.count = 0;
    this.total = 0;
    this.price = 0.00;
    this.discount = 0.00;
    this.points = 0.00;
    
    this.goods = this.goods.sort((item1, item2) => item1.count && !item2.count ? -1 : 
      !item1.count && item2.count ? 1 : 
      item1.title < item2.title ? -1 : 
      item1.title > item2.title ? 1 : 
      0);
    
    this.goods.forEach((item) => {
      this.currency = item.amount?.currency?.id || this.replaceCurrency || 1;
      this.modifiable += item.count;
      this.count += item.count;
      this.total += item.total;
      
      let price = item.price?.value * item.total;
      let discount = price - item.group.reduce((prev, gr) => prev + gr.amount * gr.count, 0.00);
      
      if (discount < 0.00) {
        this.price += -discount;
      } else {
        this.price += price;
        this.discount += discount;
      }

      this.points += item.points;
    });
  }
  
  _syncData(data) {
    this.message = data.message;
    this.isNext = data.is_next;
    
    this.goods.length = 0;
    
    data.list.forEach((item) => {
      let id = Number(item.goods.id);
      let count = Number(item.count);
      
      if (count <= 0)
        return;
      
      let goods = this.find(id);
      
      if (!goods) {
        this.goods.push(this._buildGoodsItem(item.goods));
        
        goods = this.goods[this.goods.length - 1];
      }
      
      goods.group.push({ 
          count: count, 
          amount: item.goods.amount.value, 
          points: item.goods.points, 
          is_modifiable: item.is_modifiable 
        });
      
      if (item.is_modifiable)
        goods.count += count;
      
      goods.points += item.goods.points * item.count;
      
      goods.total += count;
    });
    
    this.setFetched(true);
    
    this._calcValues();
    
    this.store();
  }
  
  _setObject(data) {
    this.object = data;
  }
  
  _buildGoodsItem(goods) {
    return {
        id: goods.goods ? goods.goods.id : goods.id,
        code: goods.goods ? goods.goods.code : goods.code,
        title: goods.goods ? goods.goods.title : goods.title,
        description: goods.goods ? goods.goods.description : goods.description,
        
        photo: goods.goods ? goods.goods.photo : goods.photo,
        agreement_article_list: goods.goods ? goods.goods.agreement_article_list : goods.agreement_article_list,
        
        is_delivery_required: goods.goods ? goods.goods.is_delivery_required : goods.is_delivery_required,
        is_excluded_from_store_accounting: goods.goods ? goods.goods.is_excluded_from_store_accounting : goods.is_excluded_from_store_accounting,
        
        price: goods.price,
        discount: goods.discount,
        amount: goods.amount,
        points: 0,
        
        count: 0,
        total: 0,
        group: []
      };
  }
};

decorate(CartStore, {
  goods: observable,
  isFetched: observable,
  lastModified: observable,
  modifiable: observable,
  count: observable,
  total: observable,
  currency: observable,
  replaceCurrency: observable,
  price: observable,
  discount: observable,
  points: observable,
  object: observable,
  message: observable,
  isNext: observable,
  isVisible: observable,
  modalRef: observable,
  modalBtnRef: observable,

  length: computed,
  agreement_article_list: computed,
  is_delivery_required: computed,

  setFetched: action,
  setVisible: action,
  setReplaceCurrency: action,
  clearSubmit: action,
  add: action,
  set: action,
  load: action,
  setModalRef: action,
  setModalBtnRef: action,
  
  _calcValues: action,
  _syncData: action,
  _setObject: action
});
